import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  HStack,
  VStack,
  useToast,
  Box,
  Text,
  Badge,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Code,
  Textarea,
  Spinner,
  IconButton,
  Tooltip,
  Select,
} from '@chakra-ui/react';
import { X, CheckCircle, Clock, Image, Sparkles, Shield, FileCode, Video, RefreshCw, Edit3, Copy, Check, User, ShieldAlert, Smartphone } from 'lucide-react';
import DOMPurify from 'dompurify';
import type { Draft } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import { approveDraft, rejectDraft, regenerateImage, listWPAuthors, getFeaturedImage } from '../lib/api';
import type { WPAuthor, FeaturedImage } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import ComplianceReport from './ComplianceReport';
import ShortFormContent from './ShortFormContent';

function QualityBadge({ score }: { score: number | null }) {
  if (score == null) return null;
  const color = score >= 80 ? 'green' : score >= 60 ? 'yellow' : 'red';
  const label = score >= 80 ? 'High' : score >= 60 ? 'Medium' : 'Low';
  return (
    <Badge
      colorScheme={color}
      px={3}
      py={1}
      borderRadius="full"
      fontSize="sm"
      fontWeight="bold"
    >
      Quality: {score}/100 ({label})
    </Badge>
  );
}

interface DraftPreviewProps {
  isOpen: boolean;
  onClose: () => void;
  draft: Draft;
  onDraftUpdated: () => void;
}

const ESTIMATED_COST_PER_IMAGE = 0.08;

export default function DraftPreview({
  isOpen,
  onClose,
  draft: initialDraft,
  onDraftUpdated,
}: DraftPreviewProps) {
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState<Draft>(initialDraft);
  const [editingSlot, setEditingSlot] = useState<number | null>(null);
  const [editPrompt, setEditPrompt] = useState('');
  const [regeneratingSlot, setRegeneratingSlot] = useState<number | null>(null);
  const [copiedArtifact, setCopiedArtifact] = useState<string | null>(null);
  const [wpAuthors, setWpAuthors] = useState<WPAuthor[]>([]);
  const [selectedAuthorId, setSelectedAuthorId] = useState<number | undefined>(undefined);
  const [authorUserTouched, setAuthorUserTouched] = useState(false);
  const [featuredImage, setFeaturedImage] = useState<FeaturedImage | null>(null);
  const toast = useToast();
  const { user } = useAuth();
  const currentUserEmail = user?.email?.toLowerCase() ?? '';

  useEffect(() => {
    setDraft(initialDraft);
    setRegeneratingSlot(initialDraft.regenerating_image ?? null);
  }, [initialDraft]);

  useEffect(() => {
    if (!isOpen) return;

    // Reset immediately so the UI never shows the previous draft's featured
    // image / authors while this draft's fetches are still pending.
    setFeaturedImage(null);
    setWpAuthors([]);
    setSelectedAuthorId(undefined);
    setAuthorUserTouched(false);

    let cancelled = false;
    const siteId = draft.site_id || 'mgc';

    listWPAuthors(siteId)
      .then((authors) => {
        if (cancelled) return;
        setWpAuthors(authors);
        // Default the author dropdown to the logged-in user when a WP
        // author's email matches the current Supabase user's email
        // (case-insensitive). The user can still switch to anyone else;
        // this is only a sensible default so each writer's posts publish
        // under their own byline without needing to pick it every time.
        if (currentUserEmail) {
          const match = authors.find(
            (a) => (a.email ?? '').toLowerCase() === currentUserEmail
          );
          if (match) setSelectedAuthorId(match.id);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('WP authors fetch failed:', err);
        setWpAuthors([]);
      });

    getFeaturedImage(draft.id)
      .then((img) => {
        if (!cancelled) setFeaturedImage(img.url ? img : null);
      })
      .catch(() => {
        if (!cancelled) setFeaturedImage(null);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, draft.site_id, draft.id, currentUserEmail]);

  useEffect(() => {
    if (!isOpen) return;

    const channel = supabase
      .channel(`draft-regen-${draft.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'blog_posts',
          filter: `id=eq.${draft.id}`,
        },
        (payload) => {
          const updated = payload.new as Draft;
          setDraft((prev) => ({ ...prev, ...updated }));
          if (updated.regenerating_image === null && regeneratingSlot !== null) {
            // Slot 1 is the featured image — WordPress has a new featured_media
            // assignment now, so re-fetch so the preview panel reflects it.
            if (regeneratingSlot === 1) {
              getFeaturedImage(draft.id)
                .then((img) => setFeaturedImage(img.url ? img : null))
                .catch(() => setFeaturedImage(null));
            }
            setRegeneratingSlot(null);
            toast({
              title: 'Image regenerated',
              description: `Image slot ${regeneratingSlot} has been updated.`,
              status: 'success',
              duration: 4000,
            });
          }
          setRegeneratingSlot(updated.regenerating_image ?? null);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isOpen, draft.id, regeneratingSlot, toast]);

  const sanitizedHTML = useMemo(() => {
    if (!draft.content_html) return '';
    return DOMPurify.sanitize(draft.content_html, {
      ADD_TAGS: ['figure', 'figcaption', 'aside', 'details', 'summary', 'nav', 'section'],
      ADD_ATTR: ['style', 'loading', 'decoding', 'fetchpriority', 'width', 'height', 'itemscope', 'itemtype', 'itemprop', 'aria-label', 'role', 'open', 'data-placement'],
    });
  }, [draft.content_html]);

  const readTime = useMemo(() => {
    if (!draft.content_html) return 0;
    const text = draft.content_html.replace(/<[^>]*>/g, '');
    const wordCount = text.split(/\s+/).length;
    return Math.ceil(wordCount / 200);
  }, [draft.content_html]);

  const handleRegenerate = useCallback(async (slot: number, prompt: string) => {
    setRegeneratingSlot(slot);
    setEditingSlot(null);
    try {
      await regenerateImage(draft.id, slot, prompt);
      toast({
        title: 'Regenerating image...',
        description: `Slot ${slot} — this may take 15-30 seconds.`,
        status: 'info',
        duration: 5000,
      });
    } catch (error) {
      setRegeneratingSlot(null);
      toast({
        title: 'Regeneration failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    }
  }, [draft.id, toast]);

  const startEditing = useCallback((slot: number, currentPrompt: string) => {
    setEditingSlot(slot);
    setEditPrompt(currentPrompt);
  }, []);

  const copyArtifact = useCallback(async (text: string, key: string, successTitle: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedArtifact(key);
      setTimeout(() => {
        setCopiedArtifact((current) => (current === key ? null : current));
      }, 2000);
      toast({
        title: successTitle,
        status: 'success',
        duration: 2000,
      });
    } catch {
      toast({
        title: 'Copy failed',
        description: 'Browser clipboard access denied.',
        status: 'error',
        duration: 3000,
      });
    }
  }, [toast]);

  async function handleApprove() {
    try {
      setLoading(true);
      await approveDraft(draft.id, draft.wordpress_post_id, selectedAuthorId);
      
      toast({
        title: 'Draft approved!',
        description: 'Post will be published to WordPress.',
        status: 'success',
        duration: 5000,
      });
      
      onDraftUpdated();
    } catch (error) {
      toast({
        title: 'Approval failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }

  async function handleReject() {
    try {
      setLoading(true);
      await rejectDraft(draft.id);
      
      toast({
        title: 'Draft rejected',
        description: 'Draft has been marked as rejected.',
        status: 'info',
        duration: 5000,
      });
      
      onDraftUpdated();
    } catch (error) {
      toast({
        title: 'Rejection failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        maxH="90vh"
        borderRadius="2xl"
        boxShadow="2xl"
        overflow="hidden"
      >
        {/* Header */}
        <ModalHeader
          px={6}
          py={4}
          borderBottomWidth={1}
          borderColor="gray.200"
          bg="white"
        >
          <HStack justify="space-between">
            <VStack align="start" spacing={1} flex={1} pr={8}>
              <Text fontSize="xl" fontWeight="bold" color="gray.900">
                {draft.title}
              </Text>
              <HStack spacing={2} flexWrap="wrap">
                <QualityBadge score={draft.quality_score} />
                {draft.validation_report && (
                  <Badge
                    colorScheme={
                      draft.validation_report.overall_status === 'pass'
                        ? 'green'
                        : draft.validation_report.overall_status === 'warn'
                        ? 'yellow'
                        : 'red'
                    }
                    borderRadius="full"
                    fontSize="xs"
                    px={3}
                    py={1}
                    fontWeight="bold"
                    title={
                      draft.validation_report.overall_status === 'error'
                        ? 'Script has blocking framework violations — open the Compliance tab'
                        : draft.validation_report.overall_status === 'warn'
                        ? 'Script has framework warnings — review the Compliance tab'
                        : 'Script passed all framework validators'
                    }
                  >
                    {draft.validation_report.overall_status === 'pass'
                      ? '✓ Framework: pass'
                      : draft.validation_report.overall_status === 'warn'
                      ? `⚠ ${draft.validation_report.warning_count} warning${draft.validation_report.warning_count === 1 ? '' : 's'}`
                      : `✕ ${draft.validation_report.error_count} error${draft.validation_report.error_count === 1 ? '' : 's'}`}
                  </Badge>
                )}
                {draft.input_type && (
                  <Badge colorScheme="gray" borderRadius="full" fontSize="xs">
                    {draft.input_type.replace('_', ' ')}
                  </Badge>
                )}
                {(draft.site_id || draft.site_identifier) && (
                  <Badge colorScheme="blue" borderRadius="full" fontSize="xs">
                    {(draft.site_id || draft.site_identifier)?.toUpperCase()}
                  </Badge>
                )}
              </HStack>
            </VStack>
            <ModalCloseButton
              position="relative"
              right={0}
              top={0}
              color="gray.400"
              _hover={{ color: 'gray.600' }}
            />
          </HStack>
        </ModalHeader>

        <ModalBody p={0} overflowY="auto">
          <VStack align="stretch" spacing={0}>
            {/* Metadata Card */}
            <Box mx={6} mt={6} p={4} bg="gray.50" borderRadius="xl" borderWidth={1} borderColor="gray.200">
              <VStack align="stretch" spacing={2}>
                {draft.meta_description && (
                  <HStack align="start">
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" minW="140px">
                      Meta Description:
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {draft.meta_description}
                    </Text>
                  </HStack>
                )}
                {draft.focus_keyword && (
                  <HStack align="start">
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" minW="140px">
                      Focus Keyword:
                    </Text>
                    <Badge
                      bg="brand.100"
                      color="brand.700"
                      px={2}
                      py={0.5}
                      borderRadius="full"
                      fontSize="xs"
                    >
                      {draft.focus_keyword}
                    </Badge>
                  </HStack>
                )}
                {draft.secondary_keywords && draft.secondary_keywords.length > 0 && (
                  <HStack align="start">
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" minW="140px">
                      Secondary Keywords:
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {draft.secondary_keywords.join(', ')}
                    </Text>
                  </HStack>
                )}
                {readTime > 0 && (
                  <HStack align="start">
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700" minW="140px">
                      Estimated Read Time:
                    </Text>
                    <HStack spacing={1}>
                      <Clock size={14} color="#718096" />
                      <Text fontSize="sm" color="gray.600">
                        {readTime} min
                      </Text>
                    </HStack>
                  </HStack>
                )}
              </VStack>
            </Box>

            {/* Image Prompts Section — Editable with Regeneration */}
            {draft.image_prompts && draft.image_prompts.length > 0 && (
              <Box mx={6} mt={4} p={4} bg="brand.50" borderRadius="xl" borderWidth={1} borderColor="brand.200">
                <HStack mb={3} spacing={2} justify="space-between">
                  <HStack spacing={2}>
                    <Sparkles size={18} color="#bb9446" />
                    <Text fontSize="sm" fontWeight="bold" color="brand.700">
                      Image Prompts ({draft.image_prompts.length})
                    </Text>
                  </HStack>
                  <Badge colorScheme="gray" fontSize="xs" borderRadius="full">
                    ~${ESTIMATED_COST_PER_IMAGE.toFixed(2)} per regeneration
                  </Badge>
                </HStack>
                <VStack align="stretch" spacing={3}>
                  {draft.image_prompts.map((imgPrompt, index) => {
                    const slot = Number(imgPrompt.placement) || index + 1;
                    const isRegenerating = regeneratingSlot === slot;
                    const isEditing = editingSlot === slot;

                    return (
                      <Box
                        key={index}
                        p={3}
                        bg="white"
                        borderRadius="lg"
                        borderWidth={1}
                        borderColor={isRegenerating ? 'blue.300' : 'brand.100'}
                        position="relative"
                        opacity={isRegenerating ? 0.7 : 1}
                        transition="all 0.2s"
                      >
                        {isRegenerating && (
                          <HStack
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            justify="center"
                            align="center"
                            bg="whiteAlpha.800"
                            borderRadius="lg"
                            zIndex={1}
                          >
                            <Spinner size="sm" color="blue.500" />
                            <Text fontSize="xs" color="blue.600" fontWeight="semibold">
                              Regenerating...
                            </Text>
                          </HStack>
                        )}

                        <HStack mb={2} spacing={2} justify="space-between">
                          <HStack spacing={2}>
                            <Image size={14} color="#3e3e3e" />
                            <Badge
                              bg="brand.100"
                              color="brand.700"
                              fontSize="xs"
                              px={2}
                              py={0.5}
                              borderRadius="full"
                            >
                              Slot {slot}
                            </Badge>
                          </HStack>
                          <HStack spacing={1}>
                            <Tooltip label="Edit prompt" fontSize="xs">
                              <IconButton
                                aria-label="Edit prompt"
                                icon={<Edit3 size={14} />}
                                size="xs"
                                variant="ghost"
                                colorScheme="gray"
                                isDisabled={isRegenerating || !!regeneratingSlot}
                                onClick={() => startEditing(slot, imgPrompt.prompt)}
                              />
                            </Tooltip>
                            <Tooltip label={`Regenerate (~$${ESTIMATED_COST_PER_IMAGE.toFixed(2)})`} fontSize="xs">
                              <IconButton
                                aria-label="Regenerate image"
                                icon={<RefreshCw size={14} />}
                                size="xs"
                                variant="ghost"
                                colorScheme="blue"
                                isDisabled={isRegenerating || !!regeneratingSlot}
                                onClick={() => handleRegenerate(slot, imgPrompt.prompt)}
                              />
                            </Tooltip>
                          </HStack>
                        </HStack>

                        {isEditing ? (
                          <VStack align="stretch" spacing={2}>
                            <Textarea
                              value={editPrompt}
                              onChange={(e) => setEditPrompt(e.target.value)}
                              fontSize="xs"
                              minH="80px"
                              resize="vertical"
                              borderColor="blue.200"
                              _focus={{ borderColor: 'blue.400' }}
                            />
                            <HStack spacing={2} justify="flex-end">
                              <Button
                                size="xs"
                                variant="ghost"
                                onClick={() => setEditingSlot(null)}
                              >
                                Cancel
                              </Button>
                              <Button
                                size="xs"
                                colorScheme="blue"
                                leftIcon={<RefreshCw size={12} />}
                                onClick={() => handleRegenerate(slot, editPrompt)}
                                isDisabled={!editPrompt.trim()}
                              >
                                Regenerate (~${ESTIMATED_COST_PER_IMAGE.toFixed(2)})
                              </Button>
                            </HStack>
                          </VStack>
                        ) : (
                          <>
                            <Text fontSize="xs" color="gray.700" lineHeight="1.5">
                              {imgPrompt.prompt}
                            </Text>
                            {imgPrompt.alt_text && (
                              <Text fontSize="xs" color="gray.500" mt={1} fontStyle="italic">
                                Alt: {imgPrompt.alt_text}
                              </Text>
                            )}
                          </>
                        )}
                      </Box>
                    );
                  })}
                </VStack>
              </Box>
            )}

            {/* Artifact Tabs: Compliance / YouTube Script / Short-Form / Video Metadata / Schema Markup */}
            {(draft.schema_markup || draft.youtube_script || draft.youtube_metadata || draft.validation_report || draft.short_form_scripts) && (
              <Box mx={6} mt={4}>
                <Tabs variant="enclosed" colorScheme="brand" size="sm">
                  <TabList>
                    {draft.validation_report && (
                      <Tab fontSize="xs">
                        <HStack spacing={1}>
                          <ShieldAlert size={14} />
                          <span>Compliance</span>
                          {draft.validation_report.error_count > 0 && (
                            <Badge colorScheme="red" fontSize="2xs" ml={1}>
                              {draft.validation_report.error_count}
                            </Badge>
                          )}
                          {draft.validation_report.error_count === 0 &&
                            draft.validation_report.warning_count > 0 && (
                              <Badge colorScheme="yellow" fontSize="2xs" ml={1}>
                                {draft.validation_report.warning_count}
                              </Badge>
                            )}
                          {draft.validation_report.error_count === 0 &&
                            draft.validation_report.warning_count === 0 && (
                              <Badge colorScheme="green" fontSize="2xs" ml={1}>
                                ✓
                              </Badge>
                            )}
                        </HStack>
                      </Tab>
                    )}
                    {draft.youtube_script && (
                      <Tab fontSize="xs">
                        <HStack spacing={1}>
                          <Video size={14} />
                          <span>YouTube Script</span>
                        </HStack>
                      </Tab>
                    )}
                    {draft.short_form_scripts &&
                      Array.isArray(draft.short_form_scripts.scripts) &&
                      draft.short_form_scripts.scripts.length > 0 && (
                        <Tab fontSize="xs">
                          <HStack spacing={1}>
                            <Smartphone size={14} />
                            <span>Short-Form</span>
                            {/* Tiny status dot on the tab for at-a-glance signal:
                                red if errors, yellow if warnings, green if clean. */}
                            {(() => {
                              const scripts = draft.short_form_scripts!.scripts;
                              const errors = scripts.reduce(
                                (sum, script) => sum + script.validation.errors.length,
                                0
                              );
                              const warnings = scripts.reduce(
                                (sum, script) => sum + script.validation.warnings.length,
                                0
                              );
                              if (errors > 0) {
                                return (
                                  <Badge colorScheme="red" fontSize="2xs" ml={1}>
                                    {errors}
                                  </Badge>
                                );
                              }
                              if (warnings > 0) {
                                return (
                                  <Badge colorScheme="yellow" fontSize="2xs" ml={1}>
                                    {warnings}
                                  </Badge>
                                );
                              }
                              return (
                                <Badge colorScheme="green" fontSize="2xs" ml={1}>
                                  ✓
                                </Badge>
                              );
                            })()}
                          </HStack>
                        </Tab>
                      )}
                    {draft.youtube_metadata && (
                      <Tab fontSize="xs">
                        <HStack spacing={1}>
                          <Shield size={14} />
                          <span>Video Metadata</span>
                        </HStack>
                      </Tab>
                    )}
                    {draft.schema_markup && (
                      <Tab fontSize="xs">
                        <HStack spacing={1}>
                          <FileCode size={14} />
                          <span>Schema Markup</span>
                        </HStack>
                      </Tab>
                    )}
                  </TabList>
                  <TabPanels>
                    {draft.validation_report && (
                      <TabPanel p={3}>
                        <ComplianceReport report={draft.validation_report} />
                      </TabPanel>
                    )}
                    {draft.youtube_script && (
                      <TabPanel p={3}>
                        <Box position="relative">
                          <Tooltip label="Copy script to clipboard">
                            <IconButton
                              aria-label="Copy YouTube script"
                              icon={
                                copiedArtifact === 'youtube-script' ? <Check size={14} /> : <Copy size={14} />
                              }
                              size="xs"
                              variant="ghost"
                              position="absolute"
                              top={2}
                              right={2}
                              zIndex={1}
                              colorScheme={copiedArtifact === 'youtube-script' ? 'green' : 'gray'}
                              onClick={() =>
                                copyArtifact(
                                  draft.youtube_script || '',
                                  'youtube-script',
                                  'YouTube script copied'
                                )
                              }
                            />
                          </Tooltip>
                          <Box
                            p={4}
                            bg="gray.50"
                            borderRadius="lg"
                            borderWidth={1}
                            borderColor="gray.200"
                            maxH="400px"
                            overflowY="auto"
                            fontSize="sm"
                            color="gray.700"
                            lineHeight="1.8"
                            whiteSpace="pre-wrap"
                          >
                            {draft.youtube_script}
                          </Box>
                        </Box>
                      </TabPanel>
                    )}
                    {draft.short_form_scripts &&
                      Array.isArray(draft.short_form_scripts.scripts) &&
                      draft.short_form_scripts.scripts.length > 0 && (
                        <TabPanel p={3}>
                          <ShortFormContent payload={draft.short_form_scripts} />
                        </TabPanel>
                      )}
                    {draft.youtube_metadata && (
                      <TabPanel p={3}>
                        <Box position="relative">
                          <Tooltip label="Copy video metadata to clipboard">
                            <IconButton
                              aria-label="Copy video metadata"
                              icon={
                                copiedArtifact === 'video-metadata' ? <Check size={14} /> : <Copy size={14} />
                              }
                              size="xs"
                              variant="ghost"
                              position="absolute"
                              top={2}
                              right={2}
                              zIndex={1}
                              colorScheme={copiedArtifact === 'video-metadata' ? 'green' : 'gray'}
                              onClick={() =>
                                copyArtifact(
                                  JSON.stringify(draft.youtube_metadata, null, 2),
                                  'video-metadata',
                                  'Video metadata copied'
                                )
                              }
                            />
                          </Tooltip>
                          <Box
                            as="pre"
                            p={3}
                            bg="gray.900"
                            color="blue.300"
                            borderRadius="lg"
                            fontSize="xs"
                            overflowX="auto"
                            maxH="300px"
                            overflowY="auto"
                            whiteSpace="pre-wrap"
                            wordBreak="break-word"
                          >
                            <Code bg="transparent" color="blue.300" fontSize="xs">
                              {JSON.stringify(draft.youtube_metadata, null, 2)}
                            </Code>
                          </Box>
                        </Box>
                      </TabPanel>
                    )}
                    {draft.schema_markup && (
                      <TabPanel p={3}>
                        <Box position="relative">
                          <Tooltip label="Copy schema markup to clipboard">
                            <IconButton
                              aria-label="Copy schema markup"
                              icon={
                                copiedArtifact === 'schema-markup' ? <Check size={14} /> : <Copy size={14} />
                              }
                              size="xs"
                              variant="ghost"
                              position="absolute"
                              top={2}
                              right={2}
                              zIndex={1}
                              colorScheme={copiedArtifact === 'schema-markup' ? 'green' : 'gray'}
                              onClick={() =>
                                copyArtifact(
                                  JSON.stringify(draft.schema_markup, null, 2),
                                  'schema-markup',
                                  'Schema markup copied'
                                )
                              }
                            />
                          </Tooltip>
                          <Box
                            as="pre"
                            p={3}
                            bg="gray.900"
                            color="green.300"
                            borderRadius="lg"
                            fontSize="xs"
                            overflowX="auto"
                            maxH="300px"
                            overflowY="auto"
                            whiteSpace="pre-wrap"
                            wordBreak="break-word"
                          >
                            <Code bg="transparent" color="green.300" fontSize="xs">
                              {JSON.stringify(draft.schema_markup, null, 2)}
                            </Code>
                          </Box>
                        </Box>
                      </TabPanel>
                    )}
                  </TabPanels>
                </Tabs>
              </Box>
            )}

            <Divider my={6} />

            {/* Featured Image */}
            {featuredImage?.url && (
              <Box mx={6} mt={4}>
                <HStack mb={2} spacing={2}>
                  <Image size={16} color="#bb9446" />
                  <Text fontSize="sm" fontWeight="bold" color="brand.700">
                    Featured Image
                  </Text>
                  <Badge colorScheme="green" fontSize="xs" borderRadius="full">
                    Slot 1
                  </Badge>
                </HStack>
                <Box
                  borderRadius="xl"
                  overflow="hidden"
                  borderWidth={1}
                  borderColor="gray.200"
                  position="relative"
                >
                  <img
                    src={featuredImage.url}
                    alt={featuredImage.alt_text || draft.title}
                    style={{ width: '100%', display: 'block' }}
                  />
                  {regeneratingSlot === 1 && (
                    <HStack
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      bottom={0}
                      justify="center"
                      align="center"
                      bg="blackAlpha.500"
                    >
                      <Spinner size="md" color="white" />
                      <Text color="white" fontWeight="bold" fontSize="sm">
                        Regenerating...
                      </Text>
                    </HStack>
                  )}
                </Box>
              </Box>
            )}

            {/* Article Content */}
            <Box px={6} pb={6}>
              <Box
                p={{ base: 4, md: 8 }}
                bg="white"
                borderRadius="xl"
                borderWidth={1}
                borderColor="gray.200"
                dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
                sx={{
                  '& h1': {
                    fontSize: '2xl',
                    fontWeight: 'bold',
                    color: 'gray.900',
                    mb: 4,
                  },
                  '& h2': {
                    fontSize: 'xl',
                    fontWeight: 'semibold',
                    color: 'gray.900',
                    mt: 6,
                    mb: 3,
                  },
                  '& h3': {
                    fontSize: 'lg',
                    fontWeight: 'semibold',
                    color: 'gray.800',
                    mt: 4,
                    mb: 2,
                  },
                  '& p': {
                    color: 'gray.700',
                    lineHeight: '1.75',
                    mb: 4,
                  },
                  '& ul, & ol': {
                    pl: 6,
                    mb: 4,
                    color: 'gray.700',
                  },
                  '& li': {
                    mb: 2,
                  },
                  '& a': {
                    color: 'brand.600',
                    textDecoration: 'underline',
                    _hover: { color: 'brand.700' },
                  },
                  '& strong, & em': {
                    color: 'gray.900',
                  },
                  '& img': {
                    maxW: '100%',
                    borderRadius: 'md',
                    my: 4,
                  },
                  '& figure[data-placement]': {
                    position: 'relative',
                  },
                  ...(regeneratingSlot ? {
                    [`& figure[data-placement="${regeneratingSlot}"]::after`]: {
                      content: '"Regenerating..."',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      bg: 'blackAlpha.500',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 'sm',
                      fontWeight: 'bold',
                      borderRadius: 'md',
                    },
                  } : {}),
                }}
              />
            </Box>
          </VStack>
        </ModalBody>

        {/* Footer */}
        <ModalFooter
          px={6}
          py={4}
          borderTopWidth={1}
          borderColor="gray.200"
          bg="gray.50"
        >
          <HStack spacing={4} w="full" justify="flex-end">
            {wpAuthors.length > 0 && (
              <HStack spacing={2} mr="auto">
                <User size={14} color="gray" />
                <Select
                  size="sm"
                  w="200px"
                  borderRadius="md"
                  bg="white"
                  placeholder="Default author"
                  value={selectedAuthorId ?? ''}
                  onChange={(e) => {
                    setAuthorUserTouched(true);
                    setSelectedAuthorId(
                      e.target.value ? Number(e.target.value) : undefined
                    );
                  }}
                >
                  {wpAuthors.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}
                      {!authorUserTouched &&
                      currentUserEmail &&
                      (a.email ?? '').toLowerCase() === currentUserEmail
                        ? ' (you)'
                        : ''}
                    </option>
                  ))}
                </Select>
              </HStack>
            )}
            <Button
              px={6}
              py={3}
              borderWidth={2}
              borderColor="red.300"
              color="red.600"
              bg="white"
              fontWeight="semibold"
              borderRadius="lg"
              onClick={handleReject}
              isLoading={loading}
              leftIcon={<X size={18} />}
              _hover={{
                borderColor: 'red.400',
                color: 'red.700',
                bg: 'red.50',
              }}
              transition="all 0.2s"
            >
              Reject
            </Button>
            <Button
              px={6}
              py={3}
              bg="green.600"
              color="white"
              fontWeight="semibold"
              borderRadius="lg"
              boxShadow="md"
              onClick={handleApprove}
              isLoading={loading}
              leftIcon={<CheckCircle size={18} />}
              _hover={{
                bg: 'green.700',
                shadow: 'lg',
              }}
              transition="all 0.2s"
            >
              Approve & Publish
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
